const FRAME_SELECTOR = "turbo-frame[data-flashcard-frame]";
const ROOT_SELECTOR = "[data-flashcard-root]";
const TRANSITION_SELECTOR = "form[data-flashcard-transition]";
const LOADING_CLASS = "flashcard-loading";

function frameFor(element) {
  return element?.closest(FRAME_SELECTOR);
}

function liveRegionFor(frame) {
  return frame?.parentElement?.querySelector("[data-flashcard-live]");
}

function continuationFor(frame) {
  return frame?.parentElement?.querySelector("[data-flashcard-continuation]");
}

function announce(frame, message) {
  const liveRegion = liveRegionFor(frame);
  if (!liveRegion || !message) return;

  liveRegion.textContent = "";
  window.requestAnimationFrame(() => { liveRegion.textContent = message; });
}

function loadingElement(frame) {
  const loading = document.createElement("div");
  const direction = frame.dataset.flashcardDirection;
  loading.className = `${LOADING_CLASS}${direction ? ` is-${direction}` : ""}`;
  loading.setAttribute("role", "status");
  loading.setAttribute("aria-label", frame.dataset.flashcardLoadingLabel);
  loading.tabIndex = -1;

  const spinner = document.createElement("span");
  spinner.className = "spinner-border text-primary";
  spinner.setAttribute("aria-hidden", "true");
  loading.append(spinner);

  return loading;
}

function cardFor(element) {
  return element?.closest("[data-flashcard-card]");
}

function setCardFace(card, showBack) {
  const frontFace = card?.querySelector("[data-flashcard-front-face]");
  const backFace = card?.querySelector("[data-flashcard-back-face]");
  if (!card || !frontFace || !backFace) return;

  card.classList.toggle("is-flipped", showBack);
  frontFace.toggleAttribute("aria-hidden", showBack);
  frontFace.toggleAttribute("inert", showBack);
  backFace.toggleAttribute("aria-hidden", !showBack);
  backFace.toggleAttribute("inert", !showBack);
}

function flipCard(card, frame) {
  if (!card || card.classList.contains("is-flipped")) return;

  const backFocus = card.querySelector('[data-flashcard-focus="back"]');
  setCardFace(card, true);

  if (backFocus) backFocus.focus({ preventScroll: true });
  if (frame) announce(frame, frame.dataset.flashcardAnswerLabel || backFocus?.textContent?.trim());
}

function handleReveal(event) {
  const button = event.target.closest("[data-flashcard-reveal]");
  if (!button) return;

  flipCard(cardFor(button), frameFor(button));
}

function prepareTransition(event) {
  const form = event.target.closest?.(TRANSITION_SELECTOR);
  const frame = frameFor(form);
  if (!form || !frame) return;

  if (form.dataset.flashcardSubmitted === "true") {
    event.preventDefault();
    return;
  }

  form.dataset.flashcardSubmitted = "true";
  form.querySelectorAll("button").forEach((button) => { button.disabled = true; });

  frame.dataset.flashcardUserTransition = "true";
  frame.dataset.flashcardDirection = event.submitter?.dataset.flashcardDirection || "";
}

function beginFrameRequest(event) {
  const frame = event.target.matches?.(FRAME_SELECTOR) ? event.target : null;
  if (!frame) return;

  frame.dataset.flashcardRequestPending = "true";
  if (frame.dataset.flashcardUserTransition !== "true") return;

  frame.replaceChildren(loadingElement(frame));
  frame.querySelector(`.${LOADING_CLASS}`)?.focus({ preventScroll: true });
}

function clearRequestState(frame) {
  delete frame.dataset.flashcardRequestPending;
  delete frame.dataset.flashcardResponseReceived;
  delete frame.dataset.flashcardBusySeen;
  delete frame.dataset.flashcardUserTransition;
  delete frame.dataset.flashcardDirection;
}

function failTransition(frame) {
  const userTransition = frame.dataset.flashcardUserTransition === "true";
  frame.replaceChildren();

  if (userTransition) {
    continuationFor(frame)?.focus({ preventScroll: true });
    announce(frame, frame.dataset.flashcardUnavailableLabel);
  }

  clearRequestState(frame);
}

function inspectFrameResponse(event) {
  const frame = event.target.matches?.(FRAME_SELECTOR) ? event.target : null;
  if (!frame || frame.dataset.flashcardRequestPending !== "true") return;

  if (event.detail?.fetchResponse?.succeeded) {
    frame.dataset.flashcardResponseReceived = "true";
  } else {
    failTransition(frame);
  }
}

function finishTransition(event) {
  const frame = event.target.matches?.(FRAME_SELECTOR) ? event.target : frameFor(event.target);
  if (!frame) return;

  delete frame.dataset.flashcardRequestPending;
  delete frame.dataset.flashcardBusySeen;
  if (frame.dataset.flashcardUserTransition !== "true") {
    // Initial load or restoration: render the question side with the prototype spring-in.
    const frontFocus = frame.querySelector('[data-flashcard-focus="front"]');
    const card = frame.querySelector("[data-flashcard-card]");
    if (frontFocus && frame.querySelector(ROOT_SELECTOR)) {
      frame.querySelector(ROOT_SELECTOR)?.classList.add("is-entering");
      setCardFace(card, false);
      card?.classList.add("is-entering");
    }
    return;
  }

  const outcome = frame.querySelector("[data-flashcard-outcome]");
  const frontFocus = frame.querySelector('[data-flashcard-focus="front"]');

  if (frontFocus) {
    frame.querySelector(ROOT_SELECTOR)?.classList.add("is-entering");
    // New card arrived via advance: reset to front face and focus question
    const card = frame.querySelector("[data-flashcard-card]");
    if (card) {
      setCardFace(card, false);
      card.classList.add("is-entering");
    }
    frontFocus.focus({ preventScroll: true });
    clearRequestState(frame);
  } else if (outcome) {
    continuationFor(frame)?.focus({ preventScroll: true });
    announce(frame, outcome.textContent.trim());
    clearRequestState(frame);
  } else {
    failTransition(frame);
  }
}

function observeBusyChanges(records) {
  records.forEach((record) => {
    const frame = record.target;
    if (!frame.matches?.(FRAME_SELECTOR)) return;

    if (record.oldValue === null) {
      frame.dataset.flashcardBusySeen = "true";
      return;
    }

    if (frame.dataset.flashcardBusySeen !== "true") return;

    delete frame.dataset.flashcardBusySeen;
    window.setTimeout(() => {
      if (
        frame.isConnected &&
        frame.dataset.flashcardRequestPending === "true" &&
        frame.dataset.flashcardResponseReceived !== "true"
      ) failTransition(frame);
    }, 0);
  });
}

function pristineFrame(frame) {
  const replacement = frame.cloneNode(false);
  replacement.removeAttribute("src");
  replacement.removeAttribute("busy");
  replacement.removeAttribute("reloadable");
  clearRequestState(replacement);
  frame.replaceWith(replacement);
}

function scrubFlashcards() {
  document.querySelectorAll(FRAME_SELECTOR).forEach(pristineFrame);
}

function restoreFlashcards(event) {
  if (event.type === "pageshow" && !event.persisted) return;

  document.querySelectorAll(FRAME_SELECTOR).forEach((frame) => {
    if (frame.hasAttribute("src")) return;

    const initialUrl = frame.dataset.flashcardInitialUrl;
    if (initialUrl) frame.setAttribute("src", initialUrl);
  });
}

const previousLifecycle = window.__irawoFlashcardsLifecycle;
if (previousLifecycle) previousLifecycle();

const busyObserver = new MutationObserver(observeBusyChanges);
busyObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ["busy"],
  attributeOldValue: true,
  subtree: true,
});

document.addEventListener("click", handleReveal);
document.addEventListener("submit", prepareTransition, true);
document.addEventListener("turbo:before-fetch-request", beginFrameRequest);
document.addEventListener("turbo:before-fetch-response", inspectFrameResponse);
document.addEventListener("turbo:frame-load", finishTransition);
document.addEventListener("turbo:before-cache", scrubFlashcards);
document.addEventListener("turbo:load", restoreFlashcards);
window.addEventListener("pagehide", scrubFlashcards);
window.addEventListener("pageshow", restoreFlashcards);

window.__irawoFlashcardsLifecycle = function teardownFlashcardsLifecycle() {
  busyObserver.disconnect();
  document.removeEventListener("click", handleReveal);
  document.removeEventListener("submit", prepareTransition, true);
  document.removeEventListener("turbo:before-fetch-request", beginFrameRequest);
  document.removeEventListener("turbo:before-fetch-response", inspectFrameResponse);
  document.removeEventListener("turbo:frame-load", finishTransition);
  document.removeEventListener("turbo:before-cache", scrubFlashcards);
  document.removeEventListener("turbo:load", restoreFlashcards);
  window.removeEventListener("pagehide", scrubFlashcards);
  window.removeEventListener("pageshow", restoreFlashcards);
};
